import { formatDistanceToNowStrict } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { type FC, useCallback, useMemo, useState } from 'react';
import {
  PiArchiveLight,
  PiArrowCounterClockwiseLight,
  PiDotsThreeVertical,
  PiInfoLight,
  PiUserLight,
} from 'react-icons/pi';
import { ApiError, FunnelInteractionDirection } from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';
import type { LegacyConversationCardProps } from 'src/modules/communications/types/legacy-conversation-card.type';
import { isTestConversationDocument } from 'src/modules/communications/utils/test-conversation.util';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import Tag from 'src/modules/shared/components/atoms/tag';
import { Text } from 'src/modules/shared/components/atoms/text';
import Dropdown, {
  type DropdownItem,
} from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { usePeriodicRerender } from 'src/modules/shared/hooks/render';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { useArchiveFunnelConversation } from '../../../../hooks/api/funnels/funnel-conversations-mutations';
import { TestConversationBadge } from '../../test-conversations/test-conversation-badge';
import { LegacyConversationCardHeader } from './card-header';
import { LegacyConversationCardInteraction } from './card-interaction';

/** Preserves the previous conversation card behavior for general tenants. */
export const LegacyConversationCard: FC<LegacyConversationCardProps> = ({
  conversation,
  isSelected,
  onClick,
  className = '',
  layout = 'list',
  onOpenDetails,
}) => {
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  usePeriodicRerender(5000);

  const isArchived = !!conversation.archivedAt;
  const conversationId = Number(conversation.id);

  const { archiveConversation: toggleArchive } = useArchiveFunnelConversation(
    !isArchived,
    () => setSubmitting(false),
    (error) => {
      setSubmitting(false);

      if (error instanceof ApiError) {
        const serializedErrorBody = JSON.stringify(error.body);
        if (
          serializedErrorBody.includes(
            'Move the conversation to a nonterminal status before unarchiving'
          )
        ) {
          showAlert(
            t('communications.conversation.moveToOpenBeforeRestore'),
            'error'
          );
        }
      }
    }
  );

  const handleArchiveAction = useCallback(async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await toggleArchive(conversationId);
    } catch {
      // Error handled in hook callbacks
    } finally {
      setSubmitting(false);
    }
  }, [submitting, toggleArchive, conversationId]);

  const contactId = useMemo(() => {
    const contact = conversation.contacts?.[0];
    const contactCandidates = [contact?.id, conversation.contactId];

    for (const candidate of contactCandidates) {
      const parsedCandidate = Number(candidate);

      if (!Number.isNaN(parsedCandidate) && parsedCandidate > 0) {
        return parsedCandidate;
      }
    }

    return null;
  }, [conversation.contactId, conversation.contacts]);

  const menuItems = useMemo((): DropdownItem[] => {
    const items: DropdownItem[] = [];

    if (contactId && onOpenDetails) {
      items.push({
        icon: <PiInfoLight className="h-4 w-4" />,
        content: 'Ver detalles',
        onClick: () => onOpenDetails(contactId),
      });
    }

    items.push(
      isArchived
        ? {
            icon: <PiArrowCounterClockwiseLight className="w-4 h-4" />,
            content: 'Restaurar',
            onClick: () => void handleArchiveAction(),
            disabled: submitting,
          }
        : {
            icon: <PiArchiveLight className="w-4 h-4" />,
            content: (
              <Text
                as="span"
                className="text-danger-600 ![font-size:inherit] ![font-weight:inherit] ![line-height:inherit]"
              >
                Archivar
              </Text>
            ),
            onClick: () => void handleArchiveAction(),
            disabled: submitting,
          }
    );

    return items;
  }, [contactId, handleArchiveAction, isArchived, onOpenDetails, submitting]);

  const replyNeeded =
    conversation.lastInteraction?.direction ===
    FunnelInteractionDirection.INCOMING;

  // Status name for optional tag
  const statusQuery = useMemo(() => {
    if (!conversation.funnelId) return {};
    return createQuery()
      .equals('funnelId', Number(conversation.funnelId))
      .orderByAsc('position')
      .build();
  }, [conversation.funnelId]);
  const { statuses } = useFunnelStatuses(statusQuery, !!conversation.funnelId);
  const currentStatus = useMemo(() => {
    if (!conversation.funnelStatusId) return undefined;
    return statuses.find(
      (s) => Number(s.id) === Number(conversation.funnelStatusId)
    );
  }, [conversation.funnelStatusId, statuses]);

  const { users } = useAllPersonAndSystemUsers();
  const assignedUserName = useMemo(() => {
    if (!conversation.userId) {
      return 'Sin asignar';
    }

    const assignedUser = users.find(
      (user) => Number(user.id) === Number(conversation.userId)
    );

    if (!assignedUser) {
      return 'Sin asignar';
    }

    return formatFullName(
      assignedUser.firstName,
      assignedUser.lastName,
      assignedUser.email
    );
  }, [conversation.userId, users]);

  const formattedTime = conversation.lastInteractionAt
    ? formatDistanceToNowStrict(new Date(conversation.lastInteractionAt), {
        addSuffix: false,
        locale: es,
      })
    : '';
  const previewLines = layout === 'pipeline' ? 2 : 1;
  const attentionTag = isArchived ? (
    <Tag content="Archivada" size="xs" variant="neutral" appearance="soft" />
  ) : replyNeeded ? (
    <Tag content="Atender" size="xs" variant="warning" appearance="soft" />
  ) : null;
  const isTestConversation = isTestConversationDocument(conversation);

  return (
    <motion.div className={`w-full max-w-full ${className}`}>
      <Card
        onClick={onClick}
        size="md"
        isHighlighted={isSelected}
        className={`relative overflow-hidden px-3 py-2.5 transition-all duration-200 ${
          layout === 'pipeline' ? 'min-h-[104px]' : 'min-h-[84px]'
        } ${isArchived ? 'opacity-80' : ''}`}
        variant="neutral"
        appearance="outline"
        shadow="none"
      >
        <div className="flex h-full flex-col gap-1.5">
          <LegacyConversationCardHeader
            conversation={conversation}
            formattedTime={formattedTime}
            showTestBadge={false}
            actionSlot={
              menuItems.length > 0 ? (
                <Dropdown
                  items={menuItems}
                  placement="bottom-end"
                  mobileTitle="Acciones de conversación"
                  handler={
                    <Button
                      variant="neutral"
                      appearance="ghost"
                      size="sm"
                      isIconOnly
                      disabled={submitting}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <PiDotsThreeVertical className="h-4 w-4" />
                    </Button>
                  }
                />
              ) : null
            }
          />

          <LegacyConversationCardInteraction
            interaction={conversation.lastInteraction}
            previewLines={previewLines}
          />

          <div className="flex min-h-[1.25rem] items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {isTestConversation && (
                <TestConversationBadge size="2xs" className="flex-shrink-0" />
              )}
              {layout === 'list' && currentStatus && (
                <Tag
                  content={
                    <FunnelStatusName
                      name={currentStatus.name}
                      outcome={currentStatus.terminalOutcome}
                      size="tiny"
                    />
                  }
                  size="xs"
                  variant="neutral"
                  appearance="soft"
                />
              )}
              {attentionTag}
            </div>

            <Text
              size="tiny"
              color="secondary"
              className="ml-auto flex items-center gap-0.5 truncate text-[11px] text-secondary-600"
              title={assignedUserName}
            >
              <PiUserLight className="h-3 w-3 shrink-0" />
              {assignedUserName}
            </Text>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};
